import { useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { putStaffPayRate, type PayRateInput } from '../api/endpoints/payroll';
import { StaffPayRateSchema, type PayBasis, type PayrollRow, type StaffPayRate } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import { centsToDollarsInput } from '../lib/money';
import { parseRateInput } from '../lib/payroll';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { useVersionConflict } from '../lib/use-version-conflict';
import { isVersionConflict } from '../lib/version-conflict';
import { FormField } from './FormField';

type PayRateEditorProps = {
  row: PayrollRow;
  onDone: () => void;
};

const BASIS_OPTIONS: readonly { id: PayBasis; label: string }[] = [
  { id: 'class', label: 'Por clase' },
  { id: 'hour', label: 'Por hora' },
];

const BASIS_MISSING = 'Elige si se paga por clase o por hora.';
const SAVE_FAILED = 'No pudimos guardar la tarifa. Revisa tu conexión e intenta de nuevo.';
/** The rate has no history on the server: say so where it is changed. */
const RATE_HINT = 'Vale para toda planilla que abras después, también las pasadas. Descarga el CSV al cierre para guardarla.';
const PAYROLL_QUERY_KEY = ['business-payroll'];

type SaveInput = { staffId: string; rate: PayRateInput };

/** What the rate form holds, in the units the server compares: whole cents and the basis, or neither. */
type RateDraft = { rateCents: number | null; basis: PayBasis | null };
const RATE_DRAFT_KEYS = ['rateCents', 'basis'] as const;
const BASIS_WORDS: Record<PayBasis, string> = { class: 'Por clase', hour: 'Por hora' };

function rateDraft(rate: RateDraft): RateDraft {
  return { rateCents: rate.rateCents, basis: rate.basis };
}

function parseStaffPayRate(current: unknown): StaffPayRate | null {
  const parsed = StaffPayRateSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

/** The PUT body for a draft: the pair, or both null, and the version it was read at. Null when the pair is half empty. */
function rateInput(draft: RateDraft, version: number | undefined): PayRateInput | null {
  const withVersion = version !== undefined ? { expectedVersion: version } : {};
  if (draft.rateCents === null && draft.basis === null) return { rateCents: null, basis: null, ...withVersion };
  if (draft.rateCents === null || draft.basis === null) return null;
  return { rateCents: draft.rateCents, basis: draft.basis, ...withVersion };
}

/**
 * One coach's rate, edited inline in the Planilla: dollars typed, integer
 * cents sent, with its basis. "Quitar tarifa" sends both null. The planilla
 * reloads after a save, since the rate re-prices the whole period.
 *
 * The rate rides on the roster row's version. The PUT has no idempotency
 * ledger, so a retry whose 409 already holds what was being saved plans as
 * "nothing left to send" and closes as saved.
 */
export function PayRateEditor({ row, onDone }: PayRateEditorProps) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(row.rateCents === null ? '' : centsToDollarsInput(row.rateCents));
  const [basis, setBasis] = useState<PayBasis | null>(row.basis);
  const [amountError, setAmountError] = useState<string>();
  const [basisError, setBasisError] = useState<string>();
  const inputId = `tarifa-${row.staffId}`;
  // What the form started from and the row's version: a newer copy arriving in the background never moves
  // them, so a save always names what this person saw.
  const start = useRef({ draft: rateDraft(row), version: row.version });

  function startFrom(record: { rateCents: number | null; basis: PayBasis | null; version?: number | undefined }) {
    start.current = { draft: rateDraft(record), version: record.version };
  }

  function seedForm(draft: RateDraft) {
    setAmount(draft.rateCents === null ? '' : centsToDollarsInput(draft.rateCents));
    setBasis(draft.basis);
    setAmountError(undefined);
    setBasisError(undefined);
  }

  /** What the form holds now; null (and what to fix shown) when it does not read. */
  function formDraft(): RateDraft | null {
    const parsed = parseRateInput(amount);
    setAmountError(parsed.ok ? undefined : parsed.message);
    setBasisError(basis ? undefined : BASIS_MISSING);
    if (!parsed.ok || !basis) return null;
    return { rateCents: parsed.cents, basis };
  }

  const save = useMutationAttempt<SaveInput, StaffPayRate>({
    mutationFn: (input, key) => putStaffPayRate(input.staffId, input.rate, key),
    onSuccess: async (saved) => {
      startFrom(saved);
      await queryClient.invalidateQueries({ queryKey: PAYROLL_QUERY_KEY });
      onDone();
    },
    onError: (error, input) => {
      conflict.handle(error, { base: start.current.draft, mine: rateDraft(input.rate) });
    },
  });

  const conflict = useVersionConflict<StaffPayRate, RateDraft>({
    keys: RATE_DRAFT_KEYS,
    labels: { rateCents: 'Monto en dólares', basis: 'Se paga' },
    format: {
      rateCents: (cents) => (cents === null ? 'Sin tarifa' : formatMoneyCents(cents)),
      basis: (value) => (value === null ? 'Sin tarifa' : BASIS_WORDS[value]),
    },
    parse: parseStaffPayRate,
    toDraft: rateDraft,
    rebase: ({ draft, record }) => {
      seedForm(draft);
      startFrom(record);
    },
    save: ({ draft, version }) => {
      const rate = rateInput(draft, version);
      if (rate) save.mutate({ staffId: row.staffId, rate });
    },
    current: formDraft,
    discard: (record) => {
      startFrom(record);
      void queryClient.invalidateQueries({ queryKey: PAYROLL_QUERY_KEY });
      onDone();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const draft = formDraft();
    if (!draft) return;
    const rate = rateInput(draft, start.current.version);
    if (!rate) return;
    conflict.clear();
    save.mutate({ staffId: row.staffId, rate });
  };

  const clear = () => {
    conflict.clear();
    const rate = rateInput({ rateCents: null, basis: null }, start.current.version);
    if (rate) save.mutate({ staffId: row.staffId, rate });
  };

  return (
    <form className="payroll-rate-form" aria-label={`Tarifa de ${row.name}`} onSubmit={submit} noValidate>
      <FormField label="Monto en dólares" htmlFor={inputId} error={amountError}>
        <input
          id={inputId}
          inputMode="decimal"
          autoComplete="off"
          placeholder="25.00"
          aria-describedby={`${inputId}-note`}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </FormField>
      <div className="field">
        <div className="seg" role="group" aria-label="Se paga">
          {BASIS_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={basis === option.id}
              onClick={() => setBasis(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {basisError ? (
          <p role="alert" className="field-error">
            {basisError}
          </p>
        ) : null}
      </div>
      {/* Where the person pressed Guardar: the choice appears in place of the buttons. */}
      {conflict.notice}
      {conflict.asking ? null : (
        <div className="payroll-rate-actions">
          <button type="submit" className="btn small" disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn small ghost" onClick={onDone} disabled={save.isPending}>
            Cancelar
          </button>
          {row.rateCents !== null ? (
            <button type="button" className="btn small ghost" onClick={clear} disabled={save.isPending}>
              Quitar tarifa
            </button>
          ) : null}
        </div>
      )}
      <p className="row-sub payroll-rate-note" id={`${inputId}-note`}>
        {RATE_HINT}
      </p>
      {save.isError && !isVersionConflict(save.error) ? (
        <p role="alert" className="field-error payroll-rate-error">
          {save.error instanceof ApiError ? save.error.message : SAVE_FAILED}
        </p>
      ) : null}
    </form>
  );
}
